// Runs (FD §17, §22.1) — every agent run, grouped by what you'd want to do
// about it: watch it, decide on it, look at why it failed, or review it.
// Each row opens the Run detail page.

import { LiveDuration } from '../../components/live-duration'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { RunStatus, type Run } from '@goblic/shared'
import { runsApi } from '../../lib/api-client'
import { StatusBadge } from '../../components/status-badge'
import { RunTrackMini } from '../../components/run-track'
import {
  EmptyState,
  ErrorNote,
  LoadingRows,
  PageHeader,
  Tabs,
} from '../../components/ui'
import { useWorkspaceId } from '../../lib/auth'
import { useLookups } from '../../hooks/useLookups'
import { runInView, type RunView } from '../../lib/entities'
import { runStatusLabel } from '../../lib/run-recovery'
import { formatDuration, formatRelative, formatRunCost } from '../../lib/format'
import { RoleAvatar, sceneClass } from '../../components/goblin-art'

const EMPTY_COPY: Record<RunView, string> = {
  active: 'No agents are working right now. Start a run from a task.',
  waiting: 'No runs are waiting on a decision.',
  failed: 'No failed runs. Nice.',
  completed: 'Finished runs will show up here.',
  all: 'Runs appear here once you start one from a task.',
}

/** Every run; with `projectId` only that project's (the project's Runs section). */
export function RunsListPage({ projectId }: { projectId?: string } = {}) {
  const workspaceId = useWorkspaceId()
  const navigate = useNavigate()
  const lookups = useLookups()
  const [view, setView] = useState<RunView>('all')

  const runsQuery = useQuery({
    queryKey: ['runs', workspaceId, 'list', projectId ?? null],
    queryFn: () => runsApi.list({ workspaceId, projectId, limit: 100 }),
    enabled: workspaceId !== '',
    refetchInterval: 10_000,
  })

  const all = runsQuery.data?.items ?? []
  const inView = (v: RunView): Run[] => all.filter((r) => runInView(r, v))
  const runs = inView(view)

  return (
    <div>
      <PageHeader
        title="Runs"
        description="Each run is one agent working on one task in its own sandbox."
      />

      <div className={`panel ${sceneClass('lab')}`}>
        <div className="px-4 pt-3">
          <Tabs
            label="Run status"
            value={view}
            onChange={setView}
            options={[
              { value: 'all', label: 'All' },
              {
                value: 'active',
                label: 'Working',
                count: inView('active').length,
              },
              {
                value: 'waiting',
                label: 'Waiting for you',
                count: inView('waiting').length,
              },
              {
                value: 'failed',
                label: 'Failed',
                count: inView('failed').length,
              },
              { value: 'completed', label: 'Finished' },
            ]}
          />
        </div>

        {runsQuery.isLoading ? (
          <LoadingRows />
        ) : runsQuery.isError ? (
          <ErrorNote error={runsQuery.error} what="runs" />
        ) : runs.length === 0 ? (
          <EmptyState
            title="No runs here"
            art={view === 'failed' ? 'success' : 'empty'}
          >
            {EMPTY_COPY[view]}
          </EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Task</th>
                  <th>Progress</th>
                  <th className="hidden md:table-cell">Started</th>
                  <th className="hidden text-right md:table-cell">Duration</th>
                  <th className="hidden text-right sm:table-cell">Cost</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => {
                  return (
                    <tr
                      key={run.id}
                      data-clickable
                      onClick={() =>
                        navigate({
                          to: '/runs/$runId',
                          params: { runId: run.id },
                        })
                      }
                    >
                      <td className="max-w-[460px]">
                        <span className="flex items-center gap-3">
                          <RoleAvatar
                            roleName={lookups.roleName(run.roleId)}
                            size={32}
                          />
                          <span className="min-w-0">
                            <span className="block truncate font-medium text-ink">
                              {lookups.taskTitle(run.taskId)}
                            </span>
                            <span className="block truncate text-sm text-ink-3">
                              <span className="text-action">{lookups.agentName(run.agentId)}</span>
                              {run.projectId ? (
                                <> in <span className="text-signal">{lookups.projectName(run.projectId)}</span></>
                              ) : null}
                            </span>
                          </span>
                        </span>
                      </td>
                      <td>
                        <span className="flex flex-col gap-1">
                          <RunTrackMini
                            status={run.status}
                            hasStarted={run.startedAt != null}
                          />
                          <StatusBadge status={run.status} label={runStatusLabel(run)} />
                          {run.status !== RunStatus.HandedOff && (run.attempt ?? 1) > 1 ? (
                            <span className="text-sm text-ink-3">Took over from an earlier run</span>
                          ) : null}
                        </span>
                      </td>
                      <td className="hidden whitespace-nowrap md:table-cell">
                        {formatRelative(run.startedAt ?? run.queuedAt)}
                      </td>
                      <td className="hidden whitespace-nowrap text-right tabular-nums md:table-cell">
                        {<LiveDuration startedAt={run.startedAt} endedAt={run.endedAt} />}
                      </td>
                      <td className="hidden whitespace-nowrap text-right tabular-nums sm:table-cell">
                        {(() => {
                          const cost = formatRunCost(run, lookups.runCostDisplay)
                          return <span title={cost.tooltip}>{cost.text}</span>
                        })()}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
